import React from "react";
import {
  Box,
  Button,
  Divider,
  Grid2,
  Link,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router";
import { Modal } from "@rentablo/components";
import { LoadingButton } from "@rentablo/components";
import { FormField } from "@helpers";
import { renderFormElement } from "@helpers/renderFormElement";
import { useChangePassword } from "@rentablo/api/apiv1";

const formFields: FormField[] = [
  {
    name: "oldPassword",
    type: "password",
  },
  {
    name: "newPassword",
    type: "password",
  },
];

type Props = {
  onClose: () => void;
  isOpen: boolean;
};

export const PasswordEditModal = ({ onClose, isOpen }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate: changePassword, isPending } = useChangePassword();
  const { enqueueSnackbar } = useSnackbar();
  const isDownMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const schema = Yup.object().shape({
    oldPassword: Yup.string().required(t("form.errors.required")),
    newPassword: Yup.string()
      .required(t("form.errors.required"))
      .min(8, t("form.errors.passwordMinLength")),
  });

  const { control, handleSubmit, setError } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    defaultValues: {
      oldPassword: "",
      newPassword: "",
    },
  });

  const onSubmit = (data: any) => {
    changePassword(
      {
        updatePasswordParams: {
          currentPassword: data.oldPassword,
          newPassword: data.newPassword,
        },
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t("settings.profile.passwordEditModal.success"), {
            variant: "success",
            autoHideDuration: 3000,
          });
          onClose();
        },
        onError: (err) => {
          if (err.response?.status === 406) {
            setError("oldPassword", {
              message: err.response?.data.message,
            });
          } else {
            enqueueSnackbar(t("settings.profile.passwordEditModal.error"), {
              variant: "error",
              autoHideDuration: 3000,
            });
          }
        },
      }
    );
  };

  return (
    <Modal
      title={t("settings.profile.passwordEditModal.title")}
      subtitle={t("settings.profile.passwordEditModal.subtitle")}
      isOpen={isOpen}
      onClose={onClose}
      height="auto"
      width={600}
    >
      <Grid2 container spacing={6} mb={11}>
        <Grid2
          size={{
            xs: 12,
            md: 12,
          }}
        >
          {renderFormElement("settings.profile.form", formFields[0], control, {
            label: (
              <Box display="flex" justifyContent="space-between" width="100%">
                <Typography variant="body3">
                  {t("settings.profile.form.oldPassword")}
                </Typography>
                <Link onClick={() => navigate("/reset/password")}>
                  {t("settings.profile.passwordEditModal.forgotPassword")}
                </Link>
              </Box>
            ),
          })}
        </Grid2>
        <Grid2
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <Divider />
        </Grid2>
        <Grid2
          size={{
            xs: 12,
            md: 12,
          }}
        >
          {renderFormElement("settings.profile.form", formFields[1], control, {
            showPasswordStrength: true,
          })}
        </Grid2>
      </Grid2>
      <Box
        display="flex"
        justifyContent="flex-end"
        gap={4}
        pb={2}
        flexDirection={isDownMd ? "column" : "row"}
      >
        <Button variant="outlined" onClick={onClose} fullWidth={isDownMd}>
          {t("settings.profile.passwordEditModal.close")}
        </Button>
        <LoadingButton
          isFetching={isPending}
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          fullWidth={isDownMd}
        >
          {t("settings.profile.passwordEditModal.submit")}
        </LoadingButton>
      </Box>
    </Modal>
  );
};
